import type { ExtensionContext, VirtualScrollExtension } from './index';

import { watch } from 'vue';

import { calculatePrependCount } from '../utils/virtual-scroll-logic';

/**
 * Extension for Prepend Restoration logic.
 * Automatically maintains the current scroll position when items are prepended to the list.
 */
export function usePrependRestorationExtension<T = unknown>(): VirtualScrollExtension<T> {
  // Prepending is detected from the previous first item alone, so two scalars
  // are enough: no copy of a potentially huge list is kept or rebuilt.
  let prevFirstItem: T | undefined;
  let prevLength = 0;

  return {
    name: 'prepend-restoration',
    onInit(ctx: ExtensionContext<T>) {
      prevFirstItem = ctx.props.value.items[ 0 ];
      prevLength = ctx.props.value.items.length;

      watch(() => ctx.props.value.items, (newItems) => {
        const prependCount = ctx.props.value.restoreScrollOnPrepend
          ? calculatePrependCount(prevFirstItem, prevLength, newItems)
          : 0;

        if (prependCount > 0) {
          const direction = ctx.props.value.direction || 'vertical';
          const gap = (direction === 'horizontal'
            ? ctx.props.value.columnGap
            : ctx.props.value.gap) || 0;

          let addedSize = 0;
          for (let i = 0; i < prependCount; i++) {
            addedSize += ctx.methods.getItemBaseSize(newItems[ i ]!, i) + gap;
          }

          if (addedSize > 0) {
            ctx.methods.handleScrollCorrection(
              direction === 'horizontal'
                ? addedSize
                : 0,
              direction !== 'horizontal'
                ? addedSize
                : 0,
            );
          }
        }

        prevFirstItem = newItems[ 0 ];
        prevLength = newItems.length;
      }, { deep: false }); // Identity check is enough
    },
  };
}
